/**
 * Which of three disjoint, exhaustive things a register row is (platform
 * design §16, revised 2026-09-10): nothing purges a tenant any more, so this is what stands
 * between an operator and an unbounded list of noise.
 *
 * Pure and synchronous — computed from fields already on the row, nothing
 * fetched to decide it — because it runs once per row on every render of an
 * unbounded table.
 */

import type { OpsTenantRow } from '../api/schemas';

export type Segment =
  | 'funded' // available_credits > 0
  | 'waiting' // available <= 0 but has done something: a request, or any movement ever
  | 'inert'; // available <= 0 and has done nothing at all

export function segmentOf(row: OpsTenantRow): Segment {
  if (row.available_credits > 0) return 'funded';
  // `tracked_accounts` is always null under `PlatformAdminDep` (platform
  // design §18.4), so it can never be the fact that makes a row "waiting" --
  // an open request or a movement already on the ledger are the only signals
  // this side of the fence can actually see.
  const hasDoneSomething = row.open_credit_request || row.last_movement_at !== null;
  return hasDoneSomething ? 'waiting' : 'inert';
}

export interface SegmentCounts {
  funded: number;
  waiting: number;
  inert: number;
}

export function countSegments(rows: readonly OpsTenantRow[]): SegmentCounts {
  const counts: SegmentCounts = { funded: 0, waiting: 0, inert: 0 };
  for (const row of rows) counts[segmentOf(row)] += 1;
  return counts;
}
